import { itemFoundText } from './item-found.js';
import { EffectSchema } from './effect-schema.js';
import { Component, viewerChat } from './live-codec.js';
import { parseResource, readResource, resourceValue, resourceProperties, resourceCoordinate } from './vgui-res.js';

export function localization(bytes) {
    if (bytes.byteLength > 8 * 1024 * 1024) throw new Error('Localization size limit');
    const encoding = bytes[0] === 255 && bytes[1] === 254 ? 'utf-16le' : bytes[0] === 254 && bytes[1] === 255 ? 'utf-16be' : 'utf-8';
    const entries = parseResource(new TextDecoder(encoding, { fatal: true }).decode(bytes));
    const tokens = resourceValue(resourceValue(entries, 'lang'), 'tokens');
    return new Map((tokens ?? []).filter(e => typeof e.value === 'string').map(e => [e.key, e.value.replace(/\\x([0-9a-f]{2})/gi, (_match, code) => String.fromCharCode(parseInt(code, 16)))]));
}
const clean = value => String(value ?? '').replace(/[\x07][a-f0-9]{6}|[\x08][a-f0-9]{8}/gi, '').replace(/[\x00-\x09\x0b-\x1f\x7f-\x9f]/g, '').trim();
function expandedText(text, tokens) {
    const lookup = value => value.startsWith('#') ? tokens.get(value.slice(1).toLowerCase()) ?? value : value;
    let format = tokens.get(text.format.replace(/^#/, '').toLowerCase()) ?? text.format;
    const params = text.params.map((value, index) => text.kind === 4 || (text.kind === 0 && index < 2) || (text.kind === 2 && index === 0) ? value : lookup(value));
    const publicChat = /^#?TF_Chat_All(?:Dead|Spec)?$/i.test(text.format);
    if (/^#?TF_Chat_/i.test(format)) {
        const dead = /dead/i.test(format) ? '*DEAD* ' : '', spectator = /spec/i.test(format) ? '(Spectator) ' : '';
        format = `${dead}${spectator}\x03%s1\x01: %s2`;
    } else if (publicChat && !/[\x01-\x08]/.test(format)) {
        format = format.replace(/%s1/g, '\x03%s1\x01').replace(/%s2/g, '\x01%s2');
    } else if (text.kind === 2 && text.format === '(Voice) %s1: %s2') {
        format = '(Voice) \x03%s1\x01: %s2';
    }
    return format.replace(/%[sd](?:([1-4]))?/g, (() => {
        let next = 0;
        return (_match, index) => params[index ? Number(index) - 1 : next++] ?? '';
    })());
}
export function formatText(text, tokens = new Map()) { return clean(expandedText(text, tokens)); }

// Source color escapes select ranges, never HTML. Default/body text is white;
// team color applies only until the next reset, including literal plugin text.
export function textRuns(text, tokens = new Map()) {
    const value = expandedText(text, tokens), runs = [];
    const team = text.team === 2 ? '#ff8075' : text.team === 3 ? '#99ccff' : text.team === 1 ? '#cccccc' : '#99ff99';
    const colors = { 1: '#ffffff', 3: team, 4: '#40ff40', 5: '#b2d866', 6: '#ffffff' };
    let color = '#ffffff', buffer = '';
    const flush = () => {
        if (!buffer) return;
        if (runs.at(-1)?.color === color) runs.at(-1).text += buffer;
        else runs.push({ text: buffer, color });
        buffer = '';
    };
    for (let at = 0; at < value.length;) {
        const code = value.charCodeAt(at++);
        if (code === 7 || code === 8) {
            const count = code === 7 ? 6 : 8, hex = value.slice(at, at + count);
            if (new RegExp(`^[a-f0-9]{${count}}$`, 'i').test(hex)) { flush(); color = '#' + hex; at += count; }
        } else if (colors[code]) { flush(); color = colors[code]; }
        else if (code === 2) {
            // Legacy SayText colors the first parameter/name, then resets.
            flush(); const name = clean(text.params[0]);
            const next = name ? value.indexOf(name, at) : -1;
            if (next >= at) {
                buffer = clean(value.slice(at, next)); flush();
                color = team; buffer = name; flush(); color = '#ffffff'; at = next + name.length;
            }
        } else if (code === 10 || code === 13 || code === 9) buffer += ' ';
        else if (code >= 32 && !(code >= 127 && code <= 159)) buffer += value[at - 1];
    }
    flush();
    if (runs.length) { runs[0].text = runs[0].text.trimStart(); runs.at(-1).text = runs.at(-1).text.trimEnd(); }
    return runs.filter(run => run.text);
}

// Shipped resources wrap controls in Resource/... blocks and call the history
// HudChatHistory. Preserve file order for resolution-specific scheme variants.
export function chatResources(base, hud, scheme) {
    const controls = base?.find(e => Array.isArray(e.value))?.value ?? [];
    const hudControls = resourceValue(hud, 'resource/hudlayout.res') ?? hud;
    const layout = resourceProperties(resourceValue(controls, 'hudchathistory') ?? resourceValue(controls, 'chathistory'));
    const panel = { ...resourceProperties(resourceValue(hudControls, 'hudchat')), ...resourceProperties(resourceValue(controls, 'hudchat')) };
    const fonts = resourceValue(resourceValue(scheme, 'scheme'), 'fonts');
    const variants = resourceValue(fonts, layout.font ?? 'ChatFont');
    const inputPanel = resourceValue(controls, 'hudchatinput') ?? resourceValue(controls, 'chatinput');
    const input = resourceProperties(resourceValue(inputPanel, 'chatentry') ?? resourceValue(controls, 'chatentry'));
    return { layout, panel, input, fonts: (variants ?? []).filter(e => Array.isArray(e.value)).map(e => resourceProperties(e.value)) };
}
export function chatBounds(layout = {}, panel = {}, width, height) {
    layout ??= {}; panel ??= {};
    const scale = height / 480, referenceWidth = width / scale;
    const wide = Math.max(160, Math.min(640, resourceCoordinate(layout.wide ?? panel.wide, referenceWidth, 260)));
    const tall = Math.max(50, Math.min(200, resourceCoordinate(layout.tall, 480, 75)));
    const x = resourceCoordinate(panel.xpos, referenceWidth, 10) + resourceCoordinate(layout.xpos, wide, 10);
    const y = resourceCoordinate(panel.ypos, 480, 275) + resourceCoordinate(layout.ypos, 120, 17);
    const left = Math.max(8, Math.min(width - 8, x * scale));
    const bottom = Math.max(16, height - (y + tall) * scale);
    return { left, bottom, width: Math.max(0, Math.min(width - left - 8, wide * scale)), height: Math.min(height - bottom - 8, tall * scale) };
}

export function chatFont(fonts, height) {
    return fonts?.find(font => {
        const range = /^(\d+)\s+(\d+)$/.exec(font.yres ?? '');
        return !range || (height >= Number(range[1]) && height <= Number(range[2]));
    }) ?? fonts?.[0];
}

export class ChatState {
    constructor() { this.reset(); }
    reset(retainHistory = false) {
        if (!retainHistory) { this.seen = new Set(); this.lines = []; this.epoch = null; }
        this.visible = []; this.center = null; this.time = 0;
    }
    apply(message) {
        if (message.epoch !== undefined && message.epoch !== this.epoch) {
            this.reset(true); this.seen = new Set(); this.epoch = message.epoch;
        }
        this.time = Math.max(this.time, message.sampleTime ?? 0);
        const changes = (message.changes ?? []).filter(e => (e.components?.[Component.Text] || e.components?.[Component.ItemFound])).sort((a, b) => (a.components[Component.Text]?.startTime ?? a.components[Component.ItemFound].time) - (b.components[Component.Text]?.startTime ?? b.components[Component.ItemFound].time));
        for (const entity of changes) {
            if (this.seen.has(entity.key)) continue;
            this.seen.add(entity.key);
            if (this.seen.size > 512) this.seen.delete(this.seen.values().next().value);
            const notice = entity.components[Component.ItemFound];
            const text = entity.components[Component.Text] ?? { kind: 4, team: notice.team, startTime: notice.time, format: '%s2', params: ['','','',''], itemFound: notice };
            if (text.startTime + 15000 <= this.time) continue;
            // The publisher merges recipient copies. Different entity keys are
            // distinct sends, even when their text and timestamps are identical.
            if (text.kind === 3) this.center = text;
            else { this.lines.push(text); this.visible.push(text); }
        }
        this.lines = this.lines.slice(-64);
        this.visible = this.visible.slice(-64);
        this.frame(this.time);
    }
    frame(time) {
        if (!Number.isFinite(time)) return;
        this.time = Math.max(this.time, time);
        this.visible = this.visible.filter(text => text.startTime + 15000 > this.time);
        if (this.center && this.center.startTime + 5000 <= this.time) this.center = null;
    }
}

export class LiveChat {
    constructor({ root, center, canvas, cache, diagnostic = () => {}, onOpen = () => {}, onClose = () => {}, onSubmit = () => {} }) {
        Object.assign(this, { root, center, canvas, cache, diagnostic, onOpen, onClose, onSubmit }); this.state = new ChatState(); this.tokens = new Map();
        this.enabled = false; this.lastSubmit = -Infinity;
        this.entry = document.createElement('form'); this.entry.className = 'live-chat-entry'; this.entry.hidden = true;
        const label = document.createElement('label'); label.textContent = 'Say : '; label.htmlFor = 'live-chat-input';
        this.textbox = document.createElement('input'); this.textbox.id = 'live-chat-input'; this.textbox.type = 'text';
        this.textbox.autocomplete = 'off'; this.textbox.maxLength = 160; this.textbox.spellcheck = false;
        this.entry.append(label, this.textbox); root.after(this.entry);
        this.nodes = new WeakMap();
        this.entry.addEventListener('submit', event => { event.preventDefault(); this.submit(); });
        this.textbox.addEventListener('input', () => this.textbox.setCustomValidity(''));
        this.keydown = event => {
            if (!this.entry.hidden) {
                if (event.isComposing) return;
                if (event.code === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
                else if (event.code === 'Enter') { event.preventDefault(); event.stopPropagation(); this.submit(); }
                return;
            }
            if (!this.enabled || event.repeat || event.ctrlKey || event.altKey || event.metaKey) return;
            if (event.target?.closest?.('input, textarea, select, button, [contenteditable="true"]')) return;
            if (event.code === 'KeyT' || event.code === 'Enter') { event.preventDefault(); event.stopPropagation(); this.open(); }
        };
        document.addEventListener('keydown', this.keydown, true);
        this.resize = new ResizeObserver(() => this.render()); this.resize.observe(root.parentElement);
    }
    setEnabled(enabled) {
        this.enabled = !!enabled;
        if (!this.enabled) this.close(false);
    }
    open() {
        if (!this.enabled) return;
        this.onOpen(); this.entry.hidden = false; this.root.classList.add('live-chat-open');
        this.render(); this.root.scrollTop = this.root.scrollHeight; this.textbox.focus({ preventScroll: true });
    }
    close(focus = true) {
        const wasOpen = !this.entry.hidden;
        this.entry.hidden = true; this.root.classList.remove('live-chat-open'); this.textbox.value = '';
        this.textbox.setCustomValidity('');
        if (wasOpen) this.render();
        if (wasOpen && focus) { this.canvas?.focus({ preventScroll: true }); this.onClose(); }
    }
    submit() {
        if (!this.enabled || this.entry.hidden) return;
        const text = this.textbox.value.trim();
        if (!text) { this.close(); return; }
        try { viewerChat(text); }
        catch (error) { this.textbox.setCustomValidity(error.message); this.textbox.reportValidity(); return; }
        if (performance.now() - this.lastSubmit < 1000) {
            this.textbox.setCustomValidity('Please wait a moment before sending another message.'); this.textbox.reportValidity(); return;
        }
        this.lastSubmit = performance.now(); this.onSubmit(text); this.close();
    }
    reset(retainHistory = false) { this.close(false); this.state.reset(retainHistory); this.render(); }
    apply(message) {
        const last = this.state.lines.at(-1), count = this.state.visible.length, center = this.state.center;
        this.state.apply(message);
        if (last !== this.state.lines.at(-1) || count !== this.state.visible.length || center !== this.state.center) this.render();
    }
    frame(time) { const before = this.state.visible.length, center = this.state.center; this.state.frame(time); if ((this.entry.hidden && before !== this.state.visible.length) || center !== this.state.center) this.render(); }
    async load(assetSet) {
        if (!assetSet || this.assetSet === assetSet) return;
        this.assetSet = assetSet; this.abort?.abort(); const abort = this.abort = new AbortController();
        this.tokens = new Map(); this.layout = this.panel = this.fonts = null;
        const load = path => this.cache.fetch(`/assets/${path}?assetSet=${encodeURIComponent(assetSet)}`, abort.signal);
        const results = await Promise.allSettled([
            readResource('resource/ui/basechat.res', load),
            readResource('scripts/hudlayout.res', load),
            load('resource/tf_english.txt'),
            readResource('resource/clientscheme.res', load),
            load('scripts/items/items_game.txt').then(bytes => new EffectSchema(bytes)),
        ]);
        if (abort.signal.aborted) return;
        const [base, hud, language, scheme, items] = results;
        this.itemSchema = items.status === 'fulfilled' ? items.value : null;
        Object.assign(this, chatResources(base.status === 'fulfilled' ? base.value : [], hud.status === 'fulfilled' ? hud.value : [], scheme.status === 'fulfilled' ? scheme.value : []));
        if (language.status === 'fulfilled') {
            try { this.tokens = localization(language.value); }
            catch (error) { this.diagnostic(`Chat localization unavailable: ${error.message}`); }
        }
        if (base.status === 'rejected') this.diagnostic('Chat resource unavailable; using the default chat layout.');
        this.nodes = new WeakMap();
        this.fit(); this.render();
    }
    fit() {
        const bounds = this.root.parentElement.getBoundingClientRect();
        const box = chatBounds(this.layout, this.panel, bounds.width, bounds.height);
        this.root.style.left = `${box.left}px`;
        this.root.style.bottom = `${box.bottom}px`;
        this.root.style.width = `${box.width}px`;
        const entryHeight = Math.max(24, Math.min(40, (Number(this.input?.tall) || 20) * bounds.height / 480));
        const entryWidth = Math.min(box.width, (Number(this.input?.wide) || 10000) * bounds.height / 480);
        this.entry.style.left = `${box.left}px`; this.entry.style.bottom = `${Math.max(4, box.bottom - entryHeight - 4)}px`;
        this.entry.style.width = `${entryWidth}px`; this.entry.style.minHeight = `${entryHeight}px`;
        const font = chatFont(this.fonts, bounds.height);
        if (font) {
            this.root.style.fontFamily = `${/^[\w -]{1,60}$/.test(font.name ?? '') ? font.name : 'Arial'}, sans-serif`;
            this.root.style.fontSize = `${Math.max(12, Math.min(16, (Number(font.tall) || 14) * .9))}px`;
            this.root.style.fontWeight = '400';
        }
        this.entry.style.font = this.root.style.font;
        // VGUI's fixed height assumes its own font metrics and wrapping. Browser
        // lines can be taller, especially for multiline server notices on phones.
        const toolbar = this.root.parentElement.querySelector('#toolbar')?.getBoundingClientRect();
        const top = toolbar ? Math.max(8, toolbar.bottom - bounds.top + 8) : 8;
        const available = Math.max(0, bounds.height - box.bottom - top);
        if (this.entry.hidden) {
            // Keep complete recent lines when the viewport cannot fit all eight.
            while (this.root.children.length > 1 && this.root.scrollHeight > available)
                this.root.firstElementChild.remove();
        }
        const historyHeight = Math.min(available, this.entry.hidden ? Math.max(box.height, this.root.scrollHeight + 1) : Math.max(box.height, 200 * bounds.height / 480));
        this.root.style.maxHeight = `${historyHeight}px`;
        this.root.style.setProperty('--live-chat-history-height', `${historyHeight}px`);
        if (this.entry.hidden) this.root.scrollTop = 0;
    }
    render() {
        const open = !this.entry.hidden, scrollTop = this.root.scrollTop;
        const follow = this.root.scrollHeight - this.root.clientHeight - scrollTop <= 4;
        const anchor = open && [...this.root.children].find(node => node.offsetTop + node.offsetHeight > scrollTop);
        const anchorTop = anchor?.offsetTop;
        const nodes = (open ? this.state.lines : this.state.visible.slice(-8)).map(text => {
            if (this.nodes.has(text)) return this.nodes.get(text);
            const line = document.createElement('div');
            line.className = 'live-chat-line';
            const announcement = text.itemFound ? itemFoundText(text.itemFound, this.itemSchema, this.tokens) : text;
            if (!announcement) { line.hidden = true; this.nodes.set(text, line); return line; }
            for (const run of textRuns(announcement, this.tokens)) {
                const span = document.createElement('span'); span.style.color = run.color; span.textContent = run.text; line.append(span);
            }
            this.nodes.set(text, line); return line;
        });
        const wanted = new Set(nodes);
        for (const node of [...this.root.children]) if (!wanted.has(node)) node.remove();
        nodes.forEach((node, index) => { if (this.root.children[index] !== node) this.root.insertBefore(node, this.root.children[index] ?? null); });
        this.fit();
        if (open) this.root.scrollTop = follow ? this.root.scrollHeight : scrollTop + (anchor?.parentElement === this.root ? anchor.offsetTop - anchorTop : 0);
        this.center.hidden = !this.state.center;
        this.center.textContent = this.state.center ? formatText(this.state.center, this.tokens) : '';
    }
    dispose() { this.abort?.abort(); this.resize.disconnect(); document.removeEventListener('keydown', this.keydown, true); this.reset(); this.entry.remove(); }
}
